import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type { DomainOrderPreview, DomainOrderType } from '../types/models/DomainOrder';
import type { ApiResponseDomainOrderPreview } from '../types/api/ApiResponseDomainOrder';

interface UseDomainOrderPreviewReturn {
  preview: DomainOrderPreview | null;
  loading: boolean;
  error: string | null;
  /**
   * Lazy: compute the exact total for the given cart FQDNs. Passing an empty list clears the preview.
   * `type` selects the price matrix row — pass `'transfer'`/`'trade'` for those wizards (**required**,
   * or it prices as a registration and the total is wrong; a trade prices the transfer cells).
   * Defaults to `'registration'`.
   */
  fetchPreview: (domains: string[], type?: DomainOrderType) => Promise<void>;
}

/**
 * `GET /datacenters/domain-orders/preview?domains=<csv>&type=<registration|transfer|trade>` — read-only
 * exact pricing for the cart (no order, no payment). Returns the precise `amount_ht`/`vat_rate`/
 * `amount_ttc`, computed identically to the create endpoint and **including the client's
 * reverse-charge VAT regime**, so the recap never computes VAT client-side. The preview `data` is
 * flat; JSend success is `body.status === 'success'` with an `amount_ttc` string + `items` array,
 * never `response.ok`.
 */
export const useDomainOrderPreview = (): UseDomainOrderPreviewReturn => {
  const apiFetch = useApiFetch();
  const [preview, setPreview] = useState<DomainOrderPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPreview = useCallback(
    async (domains: string[], type: DomainOrderType = 'registration') => {
      if (domains.length === 0) {
        setPreview(null);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ domains: domains.join(',') });
        // Only the transfer/trade flows need the query param; omitting it keeps the registration
        // URL byte-for-byte as before (the backend defaults to `registration`).
        if (type !== 'registration') {
          params.set('type', type);
        }
        const response = await apiFetch(`/datacenters/domain-orders/preview?${params.toString()}`);
        const body = (await response.json().catch(() => null)) as ApiResponseDomainOrderPreview | null;

        if (
          body?.status === 'success' &&
          typeof body.data?.amount_ttc === 'string' &&
          Array.isArray(body.data?.items)
        ) {
          setPreview({
            type: body.data.type ?? type,
            currency: body.data.currency ?? 'EUR',
            vat_rate: body.data.vat_rate ?? '0.00',
            amount_ht: body.data.amount_ht ?? '0.0000',
            amount_ttc: body.data.amount_ttc,
            items: body.data.items,
          });
        } else {
          setPreview(null);
          const fieldError = typeof body?.data?.domains === 'string' ? body.data.domains : null;
          setError(fieldError ?? body?.message ?? 'Could not compute the order total. Please try again.');
        }
      } catch (err) {
        setPreview(null);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred while computing the total.');
      } finally {
        setLoading(false);
      }
    },
    [apiFetch],
  );

  return { preview, loading, error, fetchPreview };
};
